import React, { useState } from 'react';
import { getImageUrl } from '../../utils/imageUtils';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { X, ChevronLeft, ChevronRight, Grid } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ImageMosaic({ images = [], title }) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!images || images.length === 0) {
        return (
            <div className="w-full h-[400px] bg-navy-100 rounded-xl flex items-center justify-center text-navy-400">
                No images available
            </div>
        );
    }

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setIsOpen(true);
    };

    const nextImage = (e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = (e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
        <>
            {/* Desktop Mosaic Grid */}
            <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 h-[450px] rounded-xl overflow-hidden relative group">

                {/* Main Large Image (Left Half) */}
                <div
                    className="col-span-2 row-span-2 relative cursor-pointer overflow-hidden group/item"
                    onClick={() => openLightbox(0)}
                >
                    <img
                        src={getImageUrl(images[0])}
                        alt="Main View"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover/item:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/10 transition-colors" />
                </div>

                {/* Side Images */}
                {images.slice(1, 5).map((img, idx) => (
                    <div
                        key={idx}
                        className="relative cursor-pointer overflow-hidden group/item"
                        onClick={() => openLightbox(idx + 1)}
                    >
                        <img
                            src={getImageUrl(img)}
                            alt={`View ${idx + 1}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/10 transition-colors" />

                        {/* Overlay for +More images on the last visible grid item */}
                        {idx === 3 && images.length > 5 && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-medium text-xl backdrop-blur-[2px]">
                                +{images.length - 5} photos
                            </div>
                        )}
                    </div>
                ))}

                {/* Fill empty grid slots if < 5 images */}
                {[...Array(Math.max(0, 4 - (images.length - 1)))].map((_, i) => (
                    <div key={`empty-${i}`} className="bg-navy-50" />
                ))}

                <Button
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-4 right-4 gap-2 shadow-lg z-10 font-medium"
                    onClick={() => openLightbox(0)}
                >
                    <Grid className="w-4 h-4" />
                    Show all photos
                </Button>
            </div>

            {/* Mobile Single View */}
            <div className="md:hidden h-[300px] relative rounded-xl overflow-hidden">
                <img
                    src={getImageUrl(images[0])}
                    alt={title}
                    className="w-full h-full object-cover"
                    onClick={() => openLightbox(0)}
                />
                <Button
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-4 right-4 gap-2 shadow-lg"
                    onClick={() => openLightbox(0)}
                >
                    <Grid className="w-4 h-4" />
                    {images.length} photos
                </Button>
            </div>

            {/* Full Screen Lightbox */}
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="max-w-[100vw] w-screen h-screen p-0 bg-black/95 border-none flex flex-col focus:outline-none">

                    {/* Header Controls */}
                    <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center z-50 text-white bg-gradient-to-b from-black/50 to-transparent">
                        <span className="font-medium drop-shadow-md">{currentIndex + 1} / {images.length}</span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20 rounded-full"
                            onClick={() => setIsOpen(false)}
                        >
                            <X className="w-6 h-6" />
                        </Button>
                    </div>

                    {/* Main Image Area */}
                    <div className="flex-1 relative flex items-center justify-center overflow-hidden w-full h-full group/nav">
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={currentIndex}
                                src={getImageUrl(images[currentIndex])}
                                alt={`Full view ${currentIndex}`}
                                className="max-h-full max-w-full object-contain p-2 md:p-8"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                drag="x"
                                dragConstraints={{ left: 0, right: 0 }}
                                dragElastic={0.2}
                                onDragEnd={(e, { offset, velocity }) => {
                                    const swipe = Math.abs(offset.x) * velocity.x;
                                    if (swipe < -100) nextImage();
                                    else if (swipe > 100) prevImage();
                                }}
                            />
                        </AnimatePresence>

                        {/* Navigation Buttons (Desktop) */}
                        {images.length > 1 && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded-full h-12 w-12 hidden md:flex"
                                    onClick={prevImage}
                                >
                                    <ChevronLeft className="w-8 h-8" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded-full h-12 w-12 hidden md:flex"
                                    onClick={nextImage}
                                >
                                    <ChevronRight className="w-8 h-8" />
                                </Button>
                            </>
                        )}
                    </div>

                    {/* Thumbnails Footer */}
                    <div className="h-24 bg-black/80 flex items-center justify-center gap-2 px-4 py-2 overflow-x-auto w-full z-50">
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentIndex(idx)}
                                className={`transform transition-all duration-200 relative flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 ${currentIndex === idx
                                        ? 'border-white scale-110 opacity-100'
                                        : 'border-transparent opacity-50 hover:opacity-80'
                                    }`}
                            >
                                <img
                                    src={getImageUrl(img)}
                                    alt={`Thumb ${idx}`}
                                    className="w-full h-full object-cover"
                                />
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
